<script setup lang="ts">
/**
 * DOIT AI Board queue — the admin-facing review surface for staff
 * "Request a new AI" submissions. Lists pending requests by default
 * with a status filter, an inline expand-on-click row showing every
 * field the requester filled in, and quick decision buttons that
 * PATCH the request via /api/admin/ai-requests/{id}.
 *
 * Decision flow:
 *   pending → approved | rejected | in_progress
 *   approved | in_progress → done
 *
 * Notes are free-text; the AI Board uses them to communicate
 * reasoning back to the requester (who sees their queue via
 * /api/ai-requests/mine on the profile page — separate component).
 */
import { vWrite } from "../admin-read-only";
import { onMounted, ref } from "vue";
import {
  AdminError,
  downloadAiRequestScaffold,
  listAiRequests,
  updateAiRequest,
  type AiRequest,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";
import { fmtDateTime } from "../utils/datetime";

const props = defineProps<{ devUser: string }>();
const toast = useToast();
function _toastError(e: unknown, context: string): void {
  toast.error(context, e instanceof AdminError ? e.message : String(e));
}

type StatusFilter =
  | "all"
  | "pending"
  | "approved"
  | "rejected"
  | "in_progress"
  | "done";

const filter = ref<StatusFilter>("pending");
const rows = ref<AiRequest[]>([]);
const loading = ref(false);
const expanded = ref<Set<number>>(new Set());

/**
 * Where each request state sits visually. `pending` is the only one that
 * wants the reader's eye — it is the queue. The rest are outcomes, and an
 * outcome shouting is how a list of thirty finished requests ends up looking
 * like thirty problems.
 */
const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = {
  pending: "warn",
  approved: "ok",
  in_progress: "info",
  done: "muted",
  rejected: "bad",
};
const drafts = ref<Record<number, string>>({}); // notes draft per row

async function reload(): Promise<void> {
  loading.value = true;
  try {
    rows.value = await listAiRequests(
      props.devUser,
      filter.value === "all" ? undefined : filter.value,
    );
  } catch (e) {
    _toastError(e, "Could not load AI requests");
  } finally {
    loading.value = false;
  }
}

onMounted(reload);

function toggle(id: number): void {
  if (expanded.value.has(id)) expanded.value.delete(id);
  else expanded.value.add(id);
  // Trigger reactivity — Set mutation alone doesn't re-render.
  expanded.value = new Set(expanded.value);
}

async function decide(
  row: AiRequest,
  status: AiRequest["status"],
): Promise<void> {
  try {
    const updated = await updateAiRequest(
      row.id,
      { status, notes: drafts.value[row.id] ?? row.notes },
      props.devUser,
    );
    // In-place update so the row reflects the new status / decided_*
    // fields without a full reload + scroll-jump.
    const i = rows.value.findIndex((r) => r.id === row.id);
    if (i >= 0) rows.value[i] = updated;
    // If the new status doesn't match the current filter, drop the
    // row from view so the queue stays focused.
    if (filter.value !== "all" && updated.status !== filter.value) {
      rows.value = rows.value.filter((r) => r.id !== row.id);
    }
    toast.success(`Request #${row.id} → ${updated.status.replace("_", " ")}`);
  } catch (e) {
    _toastError(e, `Could not update request #${row.id}`);
  }
}

async function saveNotesOnly(row: AiRequest): Promise<void> {
  const notes = drafts.value[row.id];
  if (notes === undefined || notes === row.notes) return;
  try {
    const updated = await updateAiRequest(row.id, { notes }, props.devUser);
    const i = rows.value.findIndex((r) => r.id === row.id);
    if (i >= 0) rows.value[i] = updated;
    toast.success(`Notes saved on request #${row.id}`);
  } catch (e) {
    _toastError(e, `Could not save notes on request #${row.id}`);
  }
}

async function downloadScaffold(row: AiRequest): Promise<void> {
  try {
    await downloadAiRequestScaffold(row.id, props.devUser);
  } catch (e) {
    _toastError(e, `Could not download scaffold for request #${row.id}`);
  }
}
</script>

<template>
  <div class="queue">
    <header class="queue__header">
      <h2>AI Board queue</h2>
      <div class="filters">
        <label>
          Status
          <select v-model="filter" @change="reload">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="in_progress">In progress</option>
            <option value="rejected">Rejected</option>
            <option value="done">Done</option>
            <option value="all">All</option>
          </select>
        </label>
        <button @click="reload" :disabled="loading">Refresh</button>
      </div>
    </header>

    <p v-if="!loading && rows.length === 0" class="muted">
      No {{ filter === "all" ? "" : filter }} requests.
    </p>

    <ul v-if="!loading && rows.length > 0" class="rows">
      <li
        v-for="row in rows"
        :key="row.id"
        :class="['row', `row--status-${row.status}`]"
      >
        <div
          class="row__head"
          :aria-expanded="expanded.has(row.id)"
          @click="toggle(row.id)"
        >
          <StatusChip :tone="STATUS_TONE[row.status] ?? 'neutral'">
            {{ row.status.replace("_", " ") }}
          </StatusChip>
          <span class="row__name">
            {{ row.proposed_name || row.proposed_slug || "(unnamed)" }}
            <span class="row__id">#{{ row.id }}</span>
          </span>
          <span class="row__requester">
            {{ row.requester_email || row.requester_oid }}
          </span>
          <span class="row__created">{{ fmtDateTime(row.created_at) }}</span>
          <span class="row__chevron" aria-hidden="true">{{
            expanded.has(row.id) ? "▾" : "▸"
          }}</span>
        </div>

        <div v-if="expanded.has(row.id)" class="row__body">
          <dl class="detail">
            <dt>Use case</dt>
            <dd>{{ row.use_case }}</dd>
            <dt v-if="row.data_sources">Data sources</dt>
            <dd v-if="row.data_sources">{{ row.data_sources }}</dd>
            <dt v-if="row.target_audience">Audience</dt>
            <dd v-if="row.target_audience">{{ row.target_audience }}</dd>
            <dt v-if="row.business_case">Business case</dt>
            <dd v-if="row.business_case">{{ row.business_case }}</dd>
            <dt v-if="row.proposed_slug">Proposed slug</dt>
            <dd v-if="row.proposed_slug">
              <code>{{ row.proposed_slug }}</code>
            </dd>
            <dt v-if="row.decided_by">Decided by</dt>
            <dd v-if="row.decided_by">
              {{ row.decided_by }} · {{ fmtDateTime(row.decided_at) }}
            </dd>
          </dl>

          <label class="notes">
            <span class="notes__label">Board notes</span>
            <textarea
              :value="drafts[row.id] ?? row.notes"
              class="notes__input"
              rows="2"
              placeholder="Optional reasoning / next steps for the requester"
              @input="
                drafts[row.id] = ($event.target as HTMLTextAreaElement).value
              "
            />
          </label>

          <div class="actions">
            <button
              class="btn btn--ghost"
              :disabled="(drafts[row.id] ?? row.notes) === row.notes"
              v-write
              @click="saveNotesOnly(row)"
            >
              Save notes
            </button>
            <button
              v-if="row.status === 'approved' || row.status === 'in_progress'"
              class="btn btn--ghost"
              title="Download a choir-agent-import-ready scaffold YAML for this request"
              v-write
              @click="downloadScaffold(row)"
            >
              ⬇ Scaffold YAML
            </button>
            <div class="actions__spacer"></div>
            <button
              v-if="row.status === 'pending'"
              class="btn btn--danger"
              v-write
              @click="decide(row, 'rejected')"
            >
              Reject
            </button>
            <button
              v-if="row.status === 'pending' || row.status === 'approved'"
              class="btn"
              v-write
              @click="decide(row, 'in_progress')"
            >
              Mark in-progress
            </button>
            <button
              v-if="row.status === 'pending'"
              class="btn btn--primary"
              v-write
              @click="decide(row, 'approved')"
            >
              Approve
            </button>
            <button
              v-if="row.status === 'in_progress' || row.status === 'approved'"
              class="btn btn--primary"
              v-write
              @click="decide(row, 'done')"
            >
              Mark done
            </button>
            <button
              v-if="row.status !== 'pending'"
              class="btn btn--ghost"
              v-write
              @click="decide(row, 'pending')"
            >
              Reopen
            </button>
          </div>
        </div>
      </li>
    </ul>

    <SkeletonRows v-if="loading" :rows="5" />
  </div>
</template>

<style scoped>
.queue {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.queue__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: var(--space-3);
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}
.muted {
  color: var(--color-muted);
}

.rows {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.row {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  overflow: hidden;
}
.row__head {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr) 200px 160px 20px;
  gap: var(--space-3);
  align-items: center;
  padding: 0.7rem 1rem;
  cursor: pointer;
  font-size: 0.92rem;
}
.row__head:hover {
  background: var(--color-surface-alt);
}
.row__name {
  font-weight: 600;
  color: var(--color-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row__id {
  color: var(--color-muted);
  font-weight: 400;
  font-size: 0.85rem;
  margin-left: 6px;
}
.row__requester,
.row__created {
  color: var(--color-muted);
  font-size: 0.85rem;
}
.row__chevron {
  color: var(--color-muted);
}
.row__body {
  padding: var(--space-3) 1rem 1rem;
  border-top: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.detail {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 4px var(--space-3);
  margin: 0 0 var(--space-2);
  font-size: 0.9rem;
}
.detail dt {
  color: var(--color-muted);
  font-weight: 600;
}
.detail dd {
  margin: 0;
  color: var(--color-ink);
  white-space: pre-wrap;
}
.detail code {
  font-size: 0.85em;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  padding: 1px 6px;
  border-radius: 4px;
}

.notes {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.notes__label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-muted);
}
.notes__input {
  font: inherit;
  font-size: 0.9rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  resize: vertical;
}

.actions {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  flex-wrap: wrap;
}
.actions__spacer {
  flex: 1;
}
.btn {
  font: inherit;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.45rem 0.95rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-ink);
  cursor: pointer;
}
.btn:hover:not(:disabled) {
  background: var(--color-surface-alt);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn--primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}
.btn--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}
.btn--danger {
  color: var(--color-error);
  border-color: var(--color-error);
  background: transparent;
}
.btn--danger:hover:not(:disabled) {
  background: var(--color-error-bg);
}
.btn--ghost {
  background: transparent;
  color: var(--color-muted);
  border-color: transparent;
}
.btn--ghost:hover:not(:disabled) {
  background: var(--color-surface-alt);
  color: var(--color-ink);
}
</style>
